// PFA V2 pricing model — pure functions for per-print cost calc.

export interface PrinterPricingInputs {
  purchase_cost: number
  additional_upfront_cost: number
  estimated_life_years: number
  estimated_uptime_pct: number
  annual_maintenance_cost: number
  buffer_factor: number
  avg_wattage: number
}

export interface SettingsPricingInputs {
  electricity_rate_per_kwh: number
  labor_hourly_rate: number
  material_efficiency_factor: number
}

export interface SpoolPricingInputs {
  purchase_price: number | null
  original_weight_grams: number
}

export interface PrintUsage {
  grams_used: number
  spool: SpoolPricingInputs
}

export interface PrintCostBreakdown {
  filamentCost: number
  electricityCost: number
  machineCost: number
  laborCost: number
  totalCost: number
}

export function printerCapitalCostPerHour(p: PrinterPricingInputs): number {
  const lifetime =
    Number(p.purchase_cost) + Number(p.additional_upfront_cost)
    + Number(p.annual_maintenance_cost) * Number(p.estimated_life_years)
  const hours = 8760 * Number(p.estimated_uptime_pct) * Number(p.estimated_life_years)
  if (hours <= 0) return 0
  return lifetime / hours
}

export function printerElectricalCostPerHour(
  p: Pick<PrinterPricingInputs, 'avg_wattage'>,
  s: Pick<SettingsPricingInputs, 'electricity_rate_per_kwh'>,
): number {
  return (Number(p.avg_wattage) / 1000) * Number(s.electricity_rate_per_kwh)
}

export function printerTotalCostPerHour(
  p: PrinterPricingInputs,
  s: Pick<SettingsPricingInputs, 'electricity_rate_per_kwh'>,
): number {
  return (printerCapitalCostPerHour(p) + printerElectricalCostPerHour(p, s))
    * Number(p.buffer_factor)
}

export function filamentCostForUsage(
  gramsUsed: number,
  spool: SpoolPricingInputs,
  efficiencyFactor: number,
): number {
  if (spool.purchase_price == null) return 0
  if (spool.original_weight_grams <= 0) return 0
  const costPerGram = Number(spool.purchase_price) / Number(spool.original_weight_grams)
  return Number(gramsUsed) * costPerGram * Number(efficiencyFactor)
}

export interface ComputePrintCostsInput {
  printer: PrinterPricingInputs
  settings: SettingsPricingInputs
  printTimeHours: number
  laborMinutes: number
  usages: PrintUsage[]
}

export function computePrintCosts(input: ComputePrintCostsInput): PrintCostBreakdown {
  const { printer, settings, printTimeHours, laborMinutes, usages } = input

  const filamentCost = usages.reduce(
    (sum, u) => sum + filamentCostForUsage(u.grams_used, u.spool, settings.material_efficiency_factor),
    0,
  )
  const electricityCost = Number(printTimeHours) * printerElectricalCostPerHour(printer, settings)
  const machineCost = Number(printTimeHours) * printerTotalCostPerHour(printer, settings)
  const laborCost = (Number(laborMinutes) / 60) * Number(settings.labor_hourly_rate)
  const totalCost = filamentCost + machineCost + laborCost
  return { filamentCost, electricityCost, machineCost, laborCost, totalCost }
}

// ── Per-plate calculator ────────────────────────────────────────────────────
// The efficiency factor here inflates (material + electricity + machineWear)
// together, unlike computePrintCosts which inflates filament only. Both are
// intentional: computePrintCosts records historical print cost (kept stable for
// past records); computePlateScenario models forward-looking plate profitability
// per the PFA spec's grouping.

export interface PlateSlot {
  gramsUsed: number
  costPerKg: number
}

export interface PlateScenarioInput {
  printer: PrinterPricingInputs
  settings: SettingsPricingInputs & { default_moms_cut_pct: number }
  printTimeHours: number
  unitsPerPlate: number
  hardwareCostPerUnit: number
  handlingMinutesPerUnit: number
  salePricePerUnit: number
  momsCutPctOverride: number | null
  slots: PlateSlot[]
}

export interface PlateScenarioResult {
  materialCost: number
  electricityCost: number
  machineWearCost: number
  bufferedVariable: number
  hardwareCost: number
  laborCost: number
  totalPlateCost: number
  costPerUnit: number
  momsCutPct: number
  momsCutPerUnit: number
  netProfitPerUnit: number
  marginPct: number
  profitPerPlate: number
  profitPerHour: number
  breakEvenPerUnit: number
}

export function computePlateScenario(input: PlateScenarioInput): PlateScenarioResult {
  const {
    printer,
    settings,
    printTimeHours,
    unitsPerPlate,
    hardwareCostPerUnit,
    handlingMinutesPerUnit,
    salePricePerUnit,
    momsCutPctOverride,
    slots,
  } = input

  const hours = Number(printTimeHours)
  const units = Number(unitsPerPlate)
  const hwPerUnit = Number(hardwareCostPerUnit)
  const handlingMin = Number(handlingMinutesPerUnit)
  const salePrice = Number(salePricePerUnit)
  const efficiencyFactor = Number(settings.material_efficiency_factor)

  const materialCost = slots.reduce((sum, slot) => {
    if (slot.gramsUsed <= 0) return sum
    return sum + (Number(slot.gramsUsed) / 1000) * Number(slot.costPerKg)
  }, 0)

  const electricityCost = hours * printerElectricalCostPerHour(printer, settings)
  const machineWearCost = hours * printerCapitalCostPerHour(printer)
  const bufferedVariable = (materialCost + electricityCost + machineWearCost) * efficiencyFactor

  const hardwareCost = hwPerUnit * units
  const laborCost = ((handlingMin * units) / 60) * Number(settings.labor_hourly_rate)

  const totalPlateCost = bufferedVariable + hardwareCost + laborCost
  const costPerUnit = units > 0 ? totalPlateCost / units : 0

  const momsCutPct = momsCutPctOverride ?? settings.default_moms_cut_pct
  const momsCutPerUnit = salePrice * (momsCutPct / 100)

  const netProfitPerUnit = salePrice - costPerUnit - momsCutPerUnit
  const marginPct = salePrice > 0 ? netProfitPerUnit / salePrice : 0

  const profitPerPlate = netProfitPerUnit * units
  const profitPerHour = hours > 0 ? profitPerPlate / hours : 0

  const cutFraction = momsCutPct / 100
  const breakEvenPerUnit = cutFraction >= 1 ? Infinity : costPerUnit / (1 - cutFraction)

  return {
    materialCost,
    electricityCost,
    machineWearCost,
    bufferedVariable,
    hardwareCost,
    laborCost,
    totalPlateCost,
    costPerUnit,
    momsCutPct,
    momsCutPerUnit,
    netProfitPerUnit,
    marginPct,
    profitPerPlate,
    profitPerHour,
    breakEvenPerUnit,
  }
}
